import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  CONTRACT_TYPES_RATIFIED,
  ENDPOINTS,
  type FieldAccuracyResponse,
  type FieldAccuracyRow,
  type FieldApplicabilityByTypeResponse,
} from '@clm/shared-types';

import { fetchFieldAccuracy } from '../api/field-accuracy';
import { fetchFieldApplicabilityByType } from '../api/field-applicability-by-type';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { requireSurface } from '../routes/surfaces';
import { FIELD_ACCURACY_FORBIDDEN_COPY, TOO_FEW_COPY, orderFieldAccuracyRows } from './field-accuracy-rows';
import { NOT_MEASURED_COPY, pivotApplicabilityRows, type ApplicabilityFieldRow } from './field-applicability-rows';
import { PanelProblem } from './quality/panel';
import { usePanel } from './quality/panel-state';

/**
 * Admin **Extraction accuracy** — CLM-107 (FR-30, DR-08; R-107-1 … R-107-9).
 *
 * Per extracted field, how often a reviewer accepted the pipeline's value as
 * extracted and how many human decisions that figure rests on. One read,
 * `GET /v1/admin/field-accuracy`, gated `admin:configure` (Admin only — Legal
 * and contributors get the 403 copy) and scoped on the API side.
 *
 * ## A measurement, not a verdict
 *
 * **No threshold line and no colour for pass or fail.** CLM-108's threshold
 * lives on its own page, **Auto-approval**, which this page links to once and
 * otherwise ignores (R-108-20): every rate here is plain text in the table's own
 * colour, and nothing is sorted or toned by how good it is. A rate resting on
 * fewer than the response's `minimum_sample` decisions (R-107-7; D-2.1-2's 30,
 * decided 1 Oct 2026) reads **"too few decisions to read"** instead of a number
 * — a 1.000 over three decisions is noise. The counts are always shown, so
 * nothing is hidden.
 *
 * The figures here are scoped to the caller, and not split by an admin field's
 * definition revision, so for an edited admin field the Auto-approval page —
 * the org-wide figure under the current definition (R-108-6, D-107-1) — can
 * show a smaller `n` than this one. Each page names its own grain.
 *
 * ## One table, current row first
 *
 * One row per `(field, schema version, model)` — the model is in the grain so
 * a model change resets the sample (R-107-1, D-2.1-3). Rows are grouped by
 * field; within a field the API's `is_current` row (the grain holding the most
 * recent extraction) leads, and older grains follow **muted**, as history
 * (`orderFieldAccuracyRows`). The high-stakes badge is the word, in the accent
 * tag, never the status colours: it names FR-07's SLA set, not a judgement.
 *
 * ## Every figure is the API's (FR-27)
 *
 * The page formats; it never computes. The rate is printed to the three places
 * the API rounded it to; the counts are the audit log's.
 */

const ENDPOINT_PATH = ENDPOINTS['field-accuracy'].path;
const PANEL = 'Extraction accuracy';

/** Older grains read as history: the same content, muted. */
function tone(row: FieldAccuracyRow, content: ReactNode): ReactNode {
  return row.is_current ? content : <span className="mut">{content}</span>;
}

function rowKey(row: FieldAccuracyRow): string {
  return `${row.field_name}|${row.extraction_schema_version}|${row.extraction_model}`;
}

function fieldCell(row: FieldAccuracyRow): ReactNode {
  return (
    <>
      {tone(row, <code>{row.field_name}</code>)}
      {row.is_high_stakes ? (
        <>
          {' '}
          <span className="tag acc">High stakes</span>
        </>
      ) : null}
      {row.is_current ? null : <span className="mut"> · earlier schema or model</span>}
    </>
  );
}

function decisionsCell(row: FieldAccuracyRow): ReactNode {
  return tone(
    row,
    <>
      <span aria-hidden="true">
        {row.verified_count} / {row.corrected_count} / {row.rejected_count}
      </span>
      <span className="vh">
        {`${String(row.verified_count)} verified, ${String(row.corrected_count)} corrected, ${String(row.rejected_count)} rejected`}
      </span>
    </>,
  );
}

function rateCell(row: FieldAccuracyRow): ReactNode {
  if (!row.meets_minimum_sample || row.accuracy_rate === null) {
    return <span className="mut">{TOO_FEW_COPY}</span>;
  }
  return tone(row, row.accuracy_rate.toFixed(3));
}

function sampleCell(row: FieldAccuracyRow, minimum: number): ReactNode {
  return tone(row, row.meets_minimum_sample ? String(row.sample_size) : `${String(row.sample_size)} of ${String(minimum)}`);
}

function lastDecisionCell(row: FieldAccuracyRow): ReactNode {
  return tone(
    row,
    row.last_decision_utc === null ? '—' : <time dateTime={row.last_decision_utc}>{row.last_decision_utc}</time>,
  );
}

function columns(minimum: number): readonly DataTableColumn<FieldAccuracyRow>[] {
  return [
    { id: 'field', header: 'Field', cell: fieldCell },
    { id: 'schema', header: 'Schema version', cell: (r) => tone(r, <code>{r.extraction_schema_version}</code>) },
    { id: 'model', header: 'Model', cell: (r) => tone(r, <code>{r.extraction_model}</code>) },
    { id: 'extractions', header: 'Extractions', cell: (r) => tone(r, r.extraction_count), cellClassName: 'num' },
    { id: 'decisions', header: 'Decisions (V / C / R)', cell: decisionsCell, cellClassName: 'num' },
    { id: 'rate', header: 'Rate', cell: rateCell, cellClassName: 'num' },
    { id: 'sample', header: 'Sample', cell: (r) => sampleCell(r, minimum), cellClassName: 'num' },
    { id: 'last', header: 'Last decision', cell: lastDecisionCell },
  ];
}

const APPLICABILITY_PANEL = 'Field applicability by contract type';

/**
 * One type column's cell. Always shows the measured count alongside the
 * percentage (CLM-133 requirements §4) — never the percentage alone, so a
 * type with few measured contracts doesn't read as misleadingly clean. A
 * `cell` of `undefined` means that type has zero contracts in the caller's
 * scope, never "not yet measured".
 */
function applicabilityCell(row: ApplicabilityFieldRow, type: (typeof CONTRACT_TYPES_RATIFIED)[number]): ReactNode {
  const cell = row.byType[type];
  if (cell === undefined) {
    return <span className="mut">no {type}s in scope</span>;
  }
  if (cell.measured_contracts === 0 || cell.populated_rate === null) {
    return <span className="mut">{NOT_MEASURED_COPY}</span>;
  }
  return (
    <>
      <span aria-hidden="true">{Math.round(cell.populated_rate * 100)}%</span>
      <span className="vh">{`${String(Math.round(cell.populated_rate * 100))} percent`}</span>
      <span className="mut">
        {' '}
        (measured {cell.measured_contracts} of {cell.total_contracts})
      </span>
    </>
  );
}

function applicabilityFieldCell(row: ApplicabilityFieldRow): ReactNode {
  return (
    <>
      <code>{row.field_name}</code>
      {row.is_high_stakes ? (
        <>
          {' '}
          <span className="tag acc">High stakes</span>
        </>
      ) : null}
    </>
  );
}

function applicabilityColumns(): readonly DataTableColumn<ApplicabilityFieldRow>[] {
  return [
    { id: 'field', header: 'Field', cell: applicabilityFieldCell },
    ...CONTRACT_TYPES_RATIFIED.map(
      (type): DataTableColumn<ApplicabilityFieldRow> => ({
        id: type,
        header: type,
        cell: (r) => applicabilityCell(r, type),
        cellClassName: 'num',
      }),
    ),
  ];
}

export function FieldAccuracy() {
  const surface = requireSurface('field-accuracy');
  const { state, reload } = usePanel<FieldAccuracyResponse>((abort) => fetchFieldAccuracy(abort), []);
  const applicability = usePanel<FieldApplicabilityByTypeResponse>((abort) => fetchFieldApplicabilityByType(abort), []);

  return (
    <>
      <div className="hd">
        <div>
          <h2 id="field-accuracy-heading">{surface.label}</h2>
          <p>{surface.summary}</p>
          <div className="refs">
            {surface.requirements.map((requirement) => (
              <span key={requirement}>{requirement}</span>
            ))}
          </div>
        </div>
      </div>

      <section className="card qpanel" aria-labelledby="field-accuracy-heading">
        {state.kind === 'loading' ? (
          <p role="status" aria-busy="true">
            Reading {PANEL}…
          </p>
        ) : null}

        {state.kind === 'problem' ? (
          <PanelProblem
            panel={PANEL}
            state={state}
            endpointPath={ENDPOINT_PATH}
            onRetry={reload}
            forbiddenCopy={FIELD_ACCURACY_FORBIDDEN_COPY}
          />
        ) : null}

        {state.kind === 'ready' ? (
          <>
            <p className="mut">
              Rate = verified ÷ (verified + corrected): how often the reviewer accepted the value as extracted.
              Rejected decisions are counted but are not in the rate — a rejection means the reviewer could not
              settle the value, not that the model was wrong. A rate is shown from {state.response.minimum_sample}{' '}
              decisions (the minimum decided 1 Oct 2026); below that the row reads “{TOO_FEW_COPY}”. No threshold is
              applied on this page.
            </p>

            {/* CLM-108 (R-108-20) — the one link: the policy and its threshold live on their own page. */}
            <p className="mut" data-testid="auto-approval-link">
              Which fields skip review, and why, is on{' '}
              <Link to={requireSurface('verification-policy').path}>{requireSurface('verification-policy').label}</Link>{' '}
              — the organisation-wide figure, decided per field.
            </p>

            {state.response.scope_kind === 'practices' ? (
              <p className="banner up" role="status">
                These figures cover only the contracts in your scope, not the whole corpus.
              </p>
            ) : null}

            <DataTable
              caption="Extraction accuracy by field, schema version and model"
              columns={columns(state.response.minimum_sample)}
              rows={orderFieldAccuracyRows(state.response.rows)}
              rowKey={rowKey}
              empty={<p>No field has been extracted by the pipeline in your scope yet.</p>}
              scrollable
            />

            <p className="mut">
              Generated <time dateTime={state.response.generated_utc}>{state.response.generated_utc}</time>, from the
              audit log.
            </p>
          </>
        ) : null}
      </section>

      <section className="card qpanel" aria-labelledby="field-applicability-heading">
        <div className="hd">
          <h3 id="field-applicability-heading">{APPLICABILITY_PANEL}</h3>
          <p className="mut">
            Of the contracts we&apos;ve actually measured for each type, what share have a value for this field — a
            measurement, not a rule. Nothing here decides or hides what a contract type should have; a low or high
            number is read by a person, not acted on by the system.
          </p>
        </div>

        {applicability.state.kind === 'loading' ? (
          <p role="status" aria-busy="true">
            Reading {APPLICABILITY_PANEL}…
          </p>
        ) : null}

        {applicability.state.kind === 'problem' ? (
          <PanelProblem
            panel={APPLICABILITY_PANEL}
            state={applicability.state}
            endpointPath={ENDPOINTS['field-applicability-by-type'].path}
            onRetry={applicability.reload}
            forbiddenCopy={FIELD_ACCURACY_FORBIDDEN_COPY}
          />
        ) : null}

        {applicability.state.kind === 'ready' ? (
          <>
            {applicability.state.response.scope_kind === 'practices' ? (
              <p className="banner up" role="status">
                These figures cover only the contracts in your scope, not the whole corpus.
              </p>
            ) : null}

            <DataTable
              caption="Field fill rate by contract type"
              columns={applicabilityColumns()}
              rows={pivotApplicabilityRows(applicability.state.response.rows)}
              rowKey={(r) => r.field_name}
              empty={<p>No contract type in your scope has been measured yet.</p>}
              scrollable
            />

            <p className="mut">
              Generated{' '}
              <time dateTime={applicability.state.response.generated_utc}>
                {applicability.state.response.generated_utc}
              </time>
              . Replaces a manually-proposed applicability table — this one is measured.
            </p>
          </>
        ) : null}
      </section>
    </>
  );
}
